import {
  BasicMediaPicker,
  GetPageQuery,
  GetProjectsQuery,
  IMarketingLandingPage,
  IProject,
} from "apollo"
import { extractProperty } from "./extractProperty"
import { getLastPath } from "./getLastUrlPath"
import { GalleryItemWithAlts } from "./types"

// Generic helpers to map CMS block properties to typed objects
type PropertyValueType = "value" | "dateTime" | "mediaItems" | "links" | "raw" | "array"

type BlockProperty = {
  propertyName: string
  propertyValue: any
}

function pickByType(value: any, type: PropertyValueType | undefined) {
  switch (type) {
    case "dateTime":
      return value?.dateTime
    case "mediaItems":
      return value?.mediaItems
    case "links":
      return value?.links
    case "raw":
      return value
    case "value":
      return value?.value
    case "array":
      return Array.isArray(value) ? value : []
    default:
      return undefined
  }
}

type PropertyConfig = {
  source: string
  type?: PropertyValueType
}

type Mapping<T> = {
  [K in keyof T]?: T[K] extends Record<string, any>
    ? Mapping<T[K]> // nested mapping for nested objects
    : PropertyConfig // leaf mapping
}

function mapFirstBlockProperties<T extends Record<string, any>>(
  blocks: any[] | undefined,
  mapping: Partial<Record<keyof T, { source: string; type?: PropertyValueType }>>,
): Partial<T> {
  if (!Array.isArray(blocks) || blocks.length === 0) return {}
  const props: BlockProperty[] = blocks[0]?.properties ?? []
  const byName = new Map<string, any>()
  for (const p of props) byName.set(p.propertyName, p.propertyValue)

  const result: Partial<T> = {}
  for (const key in mapping) {
    const conf = mapping[key as keyof T]
    if (!conf) continue
    const raw = byName.get(conf.source)
    const val = pickByType(raw, conf.type)

    if (val !== undefined) {
      ;(result as any)[key] = val
    }
  }
  return result
}

function isPropertyConfig(value: any): value is PropertyConfig {
  return value && typeof value.source === "string"
}

export type RentProjectBenefitsItem = {
  iconNormal?: string
  iconSelected?: string
  title?: string
  content?: string
  smallImage?: string
  largeImage?: string
}
export type RentProjectBenefits = {
  anchorId?: string
  title?: string
  subTitle?: string
  items?: Array<RentProjectBenefitsItem>
}
export type RentProjectAdvantagesItem = {
  icon?: string
  title?: string
  text?: string
}

export type RentProjectAdvantages = {
  title?: string
  subTitle?: string
  image?: string
  items: Array<RentProjectAdvantagesItem>
}

export type RentProjectJoinBlock = {
  title?: string
  subTitle?: string
  buttonWhite?: string
  buttonBlue?: Array<{ name?: string; url?: string }>
  buttonBlueColor?: string
  buttonWhiteColor?: string
}

export type RentProjectCustomerReviewItem = {
  stars?: string
  image?: string
  title?: string
  subTitle?: string
  content?: string
}

export type RentProjectCustomerReviews = {
  title?: string
  subTitle?: string
  reviews: Array<RentProjectCustomerReviewItem>
}
export type RentProjectFAQItem = {
  accordionTitle?: string
  accordionContent?: string
}

export type RentProjectFAQ = {
  blockTitle?: string
  subTitle?: string
  items?: Array<RentProjectFAQItem>
}

export type RentProjectHeroBanner = {
  title?: string
  titleWithBg?: string
  imageForDesktop?: Array<{ url: string; type?: string; alt?: string }>
  imageForMobile?: Array<{ url: string; type?: string; alt?: string }>
  anchorButtonBlue?: Array<{ name?: string; url?: string }>
  anchorButtonBlueColor?: string
  anchorButton?: Array<{ name?: string; url?: string }>
  anchorButtonColor?: string
  disclaimer?: string
}

export type RentProjectContactText = {
  contactButton?: string
  leadButton?: string
  buttonColor?: string
  meetingButton?: string
  callButton?: Array<{ name?: string; url?: string }>
}

export type RentProjectFOMO = {
  textsBeforeTimer?: string
  timer?: string
  textAfterTimer?: string
}

export type RentProjectVisualizationItem = {
  title?: string
  buildingName?: string
  text?: string
  button?: Array<{
    name?: string
    url?: string
  }>
}
export type RentProjectVisualization = {
  anchorId?: string
  title?: string
  subTitle?: string
  imageForDesktop?: Array<{ url: string; type?: string; alt?: string }>
  imageForMobile?: Array<{ url: string; type?: string; alt?: string }>
  items?: Array<RentProjectVisualizationItem>
}

export type UndStr = string | undefined

export type ContentList = {
  id: number | string | any
  absoluteUrl: string
  key: any
  name?: string | null
  url: string
  urlSegment?: string | null
}
export type NumbersObject = {
  blockTitle?: string
  blockBottom?: string
  blockContent: Array<{
    title?: string
    number?: string
    textNextToNumber?: string
    text?: string
  }>
}

export type Address = {
  latitude: number
  longitude: number
  isEmpty: boolean
}

type Nodes = NonNullable<
  NonNullable<GetProjectsQuery["contentDescendantsByAbsoluteRoute"]>["nodes"]
>

export type LotteryType = {
  boxTitle: UndStr
  boxTextHTML: UndStr
  button: {
    name: string
    url: string
  }
}

type ApartmentsGeneral = {
  apartmentThumbnail?: string
  apartmentGallery: string[]
  apartmentVirtualTourLink?: string
  apartmentVirtualTourLinkName?: string
  apartmentContracts: string[]
  apartmentsDrawing: { select: string; drawing: string }[]
}

export const APARTMENT_TAKEN_STATUS = "משוריין"

export type Apartment = {
  url: string
  leadFormBMBYId?: number
  propertyType?: string
  floor?: string
  areaInSquareMeters?: number
  entryDate?: string
  numberOfRooms?: string
  apartmentCharacteristics: string[]
  buildingCharacteristics: string[]
  buildingCharacteristicsText?: string
  propertyImage?: string
  price?: number
  priceCurrency?: string
  blueprintOfTheApartment?: string
  moreInfoContentHTML?: string
  uploadContracts: string[]
  contractNames?: string[]
  projectPhone?: string
  generalProjectInfo?: string
  balconySquareMeter?: number
  buildingTitle?: string
  propertyTitle?: string
  orientation?: string
  gallery: string[]
  priorityID: string
  propertyModel?: string
  address?: string
  city?: string
  apartmentNumber?: number
  houseCommitee?: number
  location?: { latitude: number; longitude: number }
  umbracoName?: string
  apartmentsDrawing?: { select: string; drawing: string }[]
  assetName?: string
  emailForLead?: string
  leaveDetailsButton?: string
  marketingText?: string
  numberOfApartments?: number
  numberOfFloors?: number
}

export type ProjectPage = {
  dynamicBlocks?: any[]
  hideHeaderAndFooter?: boolean
  rentProjectFAQ?: RentProjectFAQ
  customerReviews?: RentProjectCustomerReviews
  rentProjectJoinBlock?: RentProjectJoinBlock
  rentProjectAdvantages?: RentProjectAdvantages
  rentProjectVisualization?: RentProjectVisualization
  rentProjectBenefits?: RentProjectBenefits
  rentProjectFOMO?: RentProjectFOMO
  rentProjectHeroBanner?: RentProjectHeroBanner
  companyId?: number
  leadFormBMBYId?: number
  imageGalleries: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }[]
  contactAnchor?: string
  leaveDetailsButton?: string
  moreInfoButton?: { url?: string; name?: string }
  apartments: ApartmentUmbraco[]
  url?: string
  projectType?: string
  heroBanner?: {
    desktopImage?: string
    mobileImage?: string
    videoUrl?: string
    videoMobile?: string
    thumbnail?: string
  }
  parent: Partial<ContentList>[]
  middleBanner?: {
    desktopImage?: string
    mobileImage?: string
  }
  projectTitle?: string
  country: Array<ContentList>
  city: Array<ContentList>
  phone: UndStr
  phoneName: UndStr
  contactPhone: UndStr
  miniSite: UndStr
  price?: number
  priceCurrency?: string
  priceText?: string
  architectsOfficeTitle?: string
  architectsOffice?: string
  virtualTourLink: UndStr
  virtualTourLinkName?: string
  groupSites: Array<ContentList>
  propertyDesignations: Array<ContentList>
  projectStatuses: Array<ContentList>
  textHTML?: string
  numbers?: NumbersObject
  numbersNew?: {
    number: UndStr
    textNextToNumber: UndStr
    text: UndStr
    mobileText?: UndStr
  }[]
  bottomText?: string
  moreInfo?: string
  subTitle?: string
  officeInfo: any
  contentHTML?: string
  galleryTitle?: string
  gallerySubTitle?: string
  gallery: Array<GalleryItemWithAlts>
  subTitle2?: string
  legalNote?: string
  companyText?: string
  groupCompanies: Array<ContentList>
  entrepreneursTitle?: string
  groupEntrepreneurs: Array<ContentList>
  groupPartnersTitle?: string
  groupPartners: Array<ContentList>
  getToKnowTheEnvironment: string[]
  titleGetToKnowTheEnvironment?: string
  lottery: LotteryType
  selectProjectsToShow: Array<ContentList>
  bmbyId?: number
  bmbyMeetingId?: string
  mapTitle?: string
  address?: Address
  textAboveProperty: string
  titleAboveProperty: string
  newTitleAboveProperty?: string
  seeMoreTitle: string
  seeMoreTitleNew?: string
  textUnderneathTitle: string
  hideLeadForm: boolean
  buildingCharacteristicsTitle: UndStr
  buildingCharacteristicsText: UndStr
  buildingCharacteristicsSelection: string[]
  apartmentsGeneral: ApartmentsGeneral
  priorityID?: string
  lotteryWays: {
    icon: UndStr
    title: UndStr
    description: UndStr
  }[]
  lotterRegisterTitle?: string
  formObject?: any
  projectButton: Array<{
    url?: string
    name?: string
  }>
  apartmentsTypes?: any
  emailForLead?: string
  rentProjectContactText?: RentProjectContactText
  generalProjectInfo?: string
  rentProjectInterestedNum?: number
  noAvailableApartmentsBanner?: {
    title?: string
    text?: string
    button?: { name?: string; url?: string }
  }
  marketingTextsMap?: Record<number, string>
  blocks: any[]
}

export type ProjectFilter = {
  label: string
  options: {
    id: number
    value: string
  }[]
}
export type ProjectFilters = {
  designations: ProjectFilter
  statuses: ProjectFilter
  subsidiaries: ProjectFilter
  cities: ProjectFilter
  countries: ProjectFilter
}

export type NodeObject = GetPageQuery["page"]

type SimpleContent = {
  id: number
  value: string
  key: string
}

export function extractContentList(object?: ContentList[]) {
  if (!object) return undefined
  return object.map((el) => ({
    id: el.id,
    absoluteUrl: el.absoluteUrl,
    key: el.key,
    name: el.name,
    url: el.url,
    urlSegment: el.urlSegment,
  }))
}

export type ApartmentUmbraco = {
  url: string
  propertyName?: string
  propertyType?: ContentList
  floor?: string
  areaInSquareMeters?: number
  entryDate?: string
  availableImmediately: boolean
  numberOfRooms?: string
  severalFloorsInTheBuilding?: string
  apartmentCharacteristics: string[]
  buildingCharacteristics: string[]
  buildingCharacteristicsText?: string
  propertyImage?: string
  price?: number
  priceCurrency?: string
  blueprintOfTheApartment?: string
  blueprintOfTheApartmentPdf?: string
  moreInfoTitle?: string
  moreInfoContentHTML?: string
  uploadContracts: Array<ContentList>
  uploadDocuments: Array<ContentList>
  balconySquareMeter?: number
  bmbyId?: number
  buildingTitle?: string
  propertyTitle?: string
  orientation?: string
  type?: string
  leadFormBMBYId?: number
  emailForLead?: string
}

function extractMarketing(object: NodeObject): ApartmentUmbraco {
  const propertyName = extractProperty(object, "propertyName") as UndStr
  const floor = extractProperty(object, "floor") as UndStr
  const propertyTypeObject = extractProperty(object, "propertyType") as ContentList[] | undefined
  const propertyType = extractContentList(propertyTypeObject)?.[0] || undefined
  const areaInSquareMeters = extractProperty(object, "areaInSquareMeters") as number | undefined
  const entryDate = extractProperty(object, "entryDate") as UndStr
  const availableImmediately = (extractProperty(object, "availableImmediately") as boolean) || false
  const numberOfRooms = extractProperty(object, "numberOfRooms") as UndStr
  const severalFloorsInTheBuilding = extractProperty(object, "severalFloorsInTheBuilding") as UndStr
  const apartmentCharacteristics =
    (extractProperty(object, "apartmentCharacteristics") as string[]) || []
  const buildingCharacteristics =
    (extractProperty(object, "buildingCharacteristics") as string[]) || []
  const buildingCharacteristicsText = extractProperty(
    object,
    "buildingCharacteristicsText",
  ) as UndStr
  const buildingCharacteristicsSelectionNew =
    extractProperty(object, "buildingCharacteristicsSelectionNew") || []
  const buildingCharacteristicsSelection = buildingCharacteristicsSelectionNew.map((el: any) =>
    extractProperty(el, "getToKnowItem"),
  )
  const propertyImage = extractProperty(object, "propertyImage") as UndStr
  const price = extractProperty(object, "price") as number | undefined
  const priceCurrency = extractProperty(object, "priceCurrency") as UndStr
  const blueprintOfTheApartment = extractProperty(object, "blueprintOfTheApartment") as UndStr
  const blueprintPDFObject = extractProperty(object, "blueprintOfTheApartmentPdf") as
    | { url: string }[]
    | undefined
  const blueprintOfTheApartmentPdf = blueprintPDFObject?.[0]?.url

  const moreInfoTitle = extractProperty(object, "moreInfoTitle") as UndStr
  const moreInfoContentHTML = extractProperty(object, "moreInfoContent") as UndStr
  const uploadContracts = (extractProperty(object, "uploadContracts") as ContentList[]) || []
  const uploadDocuments = (extractProperty(object, "uploadDocuments") as ContentList[]) || []
  const balconySquareMeter = extractProperty(object, "balconySquareMeter") as number | undefined
  const bmbyId = extractProperty(object, "bmbyId") as number | undefined
  const emailForLead = extractProperty(object, "emailForLead") as string | undefined

  return {
    url: object?.url as string,
    propertyName,
    floor,
    propertyType,
    areaInSquareMeters,
    entryDate,
    availableImmediately,
    numberOfRooms,
    severalFloorsInTheBuilding,
    apartmentCharacteristics,
    buildingCharacteristics,
    buildingCharacteristicsText,
    propertyImage,
    price,
    priceCurrency,
    blueprintOfTheApartment,
    blueprintOfTheApartmentPdf,
    moreInfoTitle,
    moreInfoContentHTML,
    uploadContracts,
    uploadDocuments,
    balconySquareMeter,
    bmbyId,
    emailForLead,
  }
}

function extractPropertyTypes(children: any) {
  const propertys = [] as any
  const seenIds = new Set()
  children?.forEach((el: any) => {
    const propertyType = extractMarketing(el)?.propertyType
    if (propertyType && !seenIds.has(propertyType.id)) {
      seenIds.add(propertyType.id)
      propertys.push(propertyType)
    }
  })
  return propertys
}

export function extractProject(object: NodeObject, companyId?: number): ProjectPage {
  const children = object?.children || []
  const apartments = children.map((el) => extractMarketing(el))
  const apartmentsTypes = extractPropertyTypes(children)
  const officeInfo = extractProperty(object, "officeInfo") as any
  const getToKnowArray = (extractProperty(object, "getToKnowTheEnvironment") as {}[]) || []
  const getToKnowTheEnvironment = getToKnowArray.map((el) =>
    extractProperty(el, "getToKnowItem"),
  ) as string[]
  const titleGetToKnowTheEnvironment = extractProperty(
    object,
    "titleGetToKnowTheEnvironment",
  ) as UndStr
  const buildingCharacteristicsTitle = extractProperty(
    object,
    "buildingCharacteristicsTitle",
  ) as UndStr
  const buildingCharacteristicsText = extractProperty(
    object,
    "buildingCharacteristicsText",
  ) as UndStr
  const blocks =
    (extractProperty(object, "blocks") as
      | {
          blockType: string
        }[]
      | undefined) || []
  const architectsOfficeTitle = extractProperty(object, "architectsOfficeTitle") as UndStr
  const architectsOffice = extractProperty(object, "architectsOffice") as UndStr
  const selectProjectsToShow =
    (extractProperty(object, "selectProjectsToShow") as Array<ContentList>) || []
  const parent = (extractProperty(object, "projectParent") as Array<ContentList>) || []
  const price = extractProperty(object, "price") as number | undefined
  const priceCurrency = extractProperty(object, "currency") as UndStr
  const priceText = extractProperty(object, "priceText") as UndStr

  const phoneButtonObject =
    (extractProperty(object, "phoneNumberButton") as { url?: string }[]) || undefined
  const phoneObject = (extractProperty(object, "phone") as { url?: string }[]) || undefined
  const phone = phoneButtonObject?.[0]?.url
  const phoneName = (phoneButtonObject?.[0] as any)?.name
  // Rental surfaces read `phone` and must stay on phoneNumberButton alone, so the
  // legacy field only feeds the sticky contact button on regular project pages,
  // which is the one place editors still fill it in.
  const contactPhone = phoneButtonObject?.[0]?.url || phoneObject?.[0]?.url
  const miniSiteObject = (extractProperty(object, "miniSite") as { url?: string }[]) || undefined
  const miniSite = miniSiteObject?.[0]?.url
  const virtualTourLinkObject =
    (extractProperty(object, "virtualTourLink") as { url?: string }[]) || undefined
  const virtualTourLink = virtualTourLinkObject?.[0]?.url
  const virtualTourNameObject =
    (extractProperty(object, "virtualTourLink") as { name?: string }[]) || undefined
  const virtualTourLinkName = virtualTourNameObject?.[0]?.name
  const projectType = extractProperty(object, "projectType") as UndStr
  const hideHeaderAndFooter = Boolean(extractProperty(object, "hideFooterAndHeader"))
  const dynamicBlocksSource =
    extractProperty(object, "dynamicBlocks") ?? extractProperty(object, "dynamicBlockList")
  const dynamicBlocks =
    (dynamicBlocksSource as any)?.blocks ??
    (Array.isArray(dynamicBlocksSource) ? dynamicBlocksSource : [])
  const moreInfo = extractProperty(object, "moreInfo") as UndStr
  const legalNote = extractProperty(object, "legalNoteEligibility") as UndStr
  const bmbyId = extractProperty(object, "bmbyId") as number

  let rentProjectBenefitsObject = extractProperty(object, "rentProjectBenefitsComponent") as any
  rentProjectBenefitsObject = rentProjectBenefitsObject?.[0]

  const rentProjectBenefits: RentProjectBenefits = rentProjectBenefitsObject && {
    anchorId: extractProperty(rentProjectBenefitsObject, "anchorId") as UndStr,
    title: extractProperty(rentProjectBenefitsObject, "title") as UndStr,
    subTitle: extractProperty(rentProjectBenefitsObject, "subTitle") as UndStr,
    items: (extractProperty(rentProjectBenefitsObject, "benefitsComponentItems") as any[]).map(
      (item) => ({
        iconNormal: extractProperty(item, "iconNormal") as UndStr,
        iconSelected: extractProperty(item, "iconSelected") as UndStr,
        title: extractProperty(item, "title") as UndStr,
        content: extractProperty(item, "content") as UndStr,
        smallImage: extractProperty(item, "smallImage") as UndStr,
        largeImage: extractProperty(item, "largeImage") as UndStr,
      }),
    ),
  }

  let RentProjectCustomerReviewsObject = extractProperty(
    object,
    "rentProjectCustomerReviews",
  ) as any
  RentProjectCustomerReviewsObject = RentProjectCustomerReviewsObject?.[0]

  const customerReviews: RentProjectCustomerReviews = RentProjectCustomerReviewsObject && {
    title: extractProperty(RentProjectCustomerReviewsObject, "title") as UndStr,
    subTitle: extractProperty(RentProjectCustomerReviewsObject, "subTitle") as UndStr,
    reviews: (extractProperty(RentProjectCustomerReviewsObject, "reviewsItems") as any[]).map(
      (item) => ({
        stars: extractProperty(item, "stars") as UndStr,
        image: extractProperty(item, "image") as UndStr,
        title: extractProperty(item, "title") as UndStr,
        subTitle: extractProperty(item, "subTitle") as UndStr,
        content: extractProperty(item, "content") as UndStr,
      }),
    ),
  }

  let rentProjectFAQObject = extractProperty(object, "rentProjectFqa") as any | undefined

  rentProjectFAQObject = rentProjectFAQObject?.[0]

  const rentProjectFAQ: RentProjectFAQ = rentProjectFAQObject && {
    blockTitle: extractProperty(rentProjectFAQObject, "blockTitle") as UndStr,
    subTitle: extractProperty(rentProjectFAQObject, "subTitle") as UndStr,
    items: (extractProperty(rentProjectFAQObject, "faq") as any[]).map((item) => ({
      accordionTitle: extractProperty(item, "accordionTitle") as UndStr,
      accordionContent: extractProperty(item, "accordionContent") as UndStr,
    })),
  }

  const heroBannerObject = extractProperty(object, "heroBanner") as {}[] | undefined
  const heroBanner = heroBannerObject && {
    desktopImage: extractProperty(heroBannerObject?.[0], "desktopImage") as UndStr,
    mobileImage: extractProperty(heroBannerObject?.[0], "mobileImage") as UndStr,
    videoUrl: extractProperty(heroBannerObject?.[0], "videoOptional") as UndStr,
    videoMobileUrl: extractProperty(heroBannerObject?.[0], "videoMobile") as UndStr,
    thumbnail: extractProperty(object, "thumbnailImageForLobbyPage") as UndStr,
  }
  const middleBannerObject = extractProperty(object, "middleBanner") as {}[] | undefined
  const middleBanner = {
    desktopImage: extractProperty(middleBannerObject?.[0], "desktopImage") as UndStr,
    mobileImage: extractProperty(middleBannerObject?.[0], "mobileImage") as UndStr,
  }

  let rentProjectAdvantagesObject = extractProperty(object, "rentProjectAdvantages" as string) as
    | any
    | undefined

  rentProjectAdvantagesObject = rentProjectAdvantagesObject?.[0]

  const rentProjectAdvantages: RentProjectAdvantages = rentProjectAdvantagesObject && {
    title: extractProperty(rentProjectAdvantagesObject, "title") as UndStr,
    subTitle: extractProperty(rentProjectAdvantagesObject, "subTitle") as UndStr,
    image: extractProperty(rentProjectAdvantagesObject, "image") as UndStr,
    items: (extractProperty(rentProjectAdvantagesObject, "iconList") as any[]).map((item) => ({
      icon: extractProperty(item, "icon") as UndStr,
      title: extractProperty(item, "title") as UndStr,
      text: extractProperty(item, "text") as UndStr,
    })),
  }

  const rentProjectVisualizationObject = extractProperty(
    object,
    "rentProjectVisualizationComponent" as string,
  ) as any | undefined

  const rentProjectVisualization: RentProjectVisualization | undefined =
    mapFirstBlockProperties<RentProjectVisualization>(rentProjectVisualizationObject, {
      anchorId: { source: "anchorId", type: "value" },
      title: { source: "title", type: "value" },
      subTitle: { source: "subTitle", type: "value" },
      imageForDesktop: { source: "imageForDesktop", type: "mediaItems" },
      imageForMobile: { source: "imageForMobile", type: "mediaItems" },
    })

  const rentProjectVisualizationItemsObject = extractProperty(
    rentProjectVisualizationObject?.[0],
    "rentProjectVisualizationComponentItems",
  )
  rentProjectVisualization.items =
    rentProjectVisualizationItemsObject &&
    rentProjectVisualizationItemsObject?.map((el: any) => {
      return {
        title: extractProperty(el, "title") as UndStr,
        buildingName: extractProperty(el, "buildingName") as UndStr,
        text: extractProperty(el, "text") as UndStr,
        button: extractProperty(el, "button") as UndStr,
      }
    })
  const rentProjectFOMOObject = extractProperty(object, "rentProjectFOMO") as any | undefined

  const rentProjectFOMO: RentProjectFOMO =
    rentProjectFOMOObject &&
    mapFirstBlockProperties<RentProjectFOMO>(rentProjectFOMOObject, {
      textsBeforeTimer: { source: "textsBeforeTimer", type: "value" },
      textAfterTimer: { source: "textAfterTimer", type: "value" },
      timer: { source: "timer", type: "dateTime" },
    })

  const rentProjectHeroBannerObject = extractProperty(object, "rentProjectHeroBanner") as
    | any
    | undefined

  const rentProjectHeroBanner: RentProjectHeroBanner =
    rentProjectHeroBannerObject &&
    mapFirstBlockProperties<RentProjectHeroBanner>(rentProjectHeroBannerObject, {
      title: { source: "title", type: "value" },
      titleWithBg: { source: "titleWithBg", type: "value" },
      imageForDesktop: { source: "imageForDesktop", type: "mediaItems" },
      imageForMobile: { source: "imageForMobile", type: "mediaItems" },
      anchorButtonBlue: { source: "anchorButtonBlue", type: "links" },
      anchorButtonBlueColor: { source: "anchorButtonBlueColor", type: "value" },
      anchorButton: { source: "anchorButton", type: "links" },
      anchorButtonColor: { source: "anchorButtonColor", type: "value" },
      disclaimer: { source: "disclaimer", type: "value" },
    })

  const rentProjectJoinBlockObject = extractProperty(object, "rentProjectJoinBlock") as
    | any
    | undefined
  const rentProjectJoinBlock: RentProjectJoinBlock =
    rentProjectJoinBlockObject &&
    mapFirstBlockProperties<RentProjectJoinBlock>(rentProjectJoinBlockObject, {
      title: { source: "title", type: "value" },
      subTitle: { source: "subTitle", type: "value" },
      buttonWhite: { source: "buttonWhite", type: "value" },
      buttonBlue: { source: "buttonBlue", type: "links" },
      buttonBlueColor: { source: "buttonBlueColor", type: "value" },
      buttonWhiteColor: { source: "buttonWhiteColor", type: "value" },
    })

  const projectTitle = extractProperty(object, "projectTitle") as UndStr
  const cityList = extractProperty(object, "city") as ContentList[] | undefined
  const countryList = extractProperty(object, "country") as ContentList[] | undefined
  const city = extractContentList(cityList) || []
  const country = extractContentList(countryList) || []
  const groupSitesObject = (extractProperty(object, "groupSIte") as ContentList[]) || []
  const groupSites = extractContentList(groupSitesObject) || []
  const propertyDesignationsObject = extractProperty(object, "propertyDesignations") as
    | ContentList[]
    | undefined
  const propertyDesignations = extractContentList(propertyDesignationsObject) || []
  const lotteryArray = (extractProperty(object, "lotteryBox") as {}[]) || []
  const lotteryBox = lotteryArray[0]
  const lottery = {
    boxTitle: extractProperty(lotteryBox, "boxTitle") as UndStr,
    boxTextHTML: extractProperty(lotteryBox, "boxText") as UndStr,
    button: extractProperty(lotteryBox, "button")?.[0] as {
      name: string
      url: string
    },
  }
  const lotterRegisterTitle = extractProperty(object, "lotteryRegisterWaysTitle") as UndStr
  const lotteryRegisterWaysArray = (extractProperty(object, "lotteryRegisterWays") as {}[]) || []
  const lotteryWays = lotteryRegisterWaysArray.map((el: any) => {
    const linkObject = extractProperty(el, "link") || []
    const linkName = linkObject[0]?.name as UndStr
    const linkURL = linkObject[0]?.url as UndStr
    return {
      icon: extractProperty(el, "icon") as UndStr,
      title: extractProperty(el, "title") as UndStr,
      description: extractProperty(el, "description") as UndStr,
      linkName,
      linkURL,
    }
  })
  const groupProjectStatus = extractProperty(object, "projectStatus") as ContentList[] | undefined
  const projectStatuses = extractContentList(groupProjectStatus) || []
  const textHTML = extractProperty(object, "text") as UndStr
  const numbersArray = extractProperty(object, "numbers") || []
  const pawerNumberArray = extractProperty(object, "pawerNumberNew") || []
  const blockTitle = extractProperty(numbersArray?.[0], "blockTitle") as UndStr
  const blockBottom = extractProperty(numbersArray?.[0], "bottomText") as UndStr
  const numbersBlocksArray = extractProperty(numbersArray?.[0], "blockContent") || []
  const pawerNumberBlocksArray = extractProperty(pawerNumberArray?.[0], "blockContent") || []
  const blockContent =
    numbersBlocksArray?.map((el: any) => ({
      title: extractProperty(el, "title") as UndStr,
      number: extractProperty(el, "number") as UndStr,
      textNextToNumber: extractProperty(el, "textNextToNumber") as UndStr,
      text: extractProperty(el, "text") as UndStr,
    })) || []
  const blockContentNew =
    pawerNumberBlocksArray?.map((el: any) => ({
      number: extractProperty(el, "number") as UndStr,
      textNextToNumber: extractProperty(el, "textNextToNumber") as UndStr,
      text: extractProperty(el, "text") as UndStr,
      mobileText: extractProperty(el, "mobileText") as UndStr,
    })) ||
    ([] as {
      number: UndStr
      textNextToNumber: UndStr
      text: UndStr
      mobileText?: UndStr
    }[])
  const subTitle = extractProperty(object, "subTitle") as UndStr
  const contentHTML = extractProperty(object, "content") as UndStr
  const galleryTitle = extractProperty(object, "galleryTitle") as UndStr
  const gallerySubTitle = extractProperty(object, "gallerySubTitle") as UndStr
  const galleryArray = extractProperty(object, "gallery") || []
  const gallery =
    galleryArray?.map((el: any) => {
      const mob = extractProperty(el, "imageOrVideoSelectorMobaile", true)?.[0]
      const desk = extractProperty(el, "imageOrVideoSelsctor", true)?.[0]
      return {
        imageOrVideoSelectorMobaile: mob?.url as UndStr,
        imageOrVideoSelsctor: desk?.url as UndStr,
        imageOrVideoSelectorMobaileAlt: mob?.altText as UndStr,
        imageOrVideoSelsctorAlt: desk?.altText as UndStr,
      }
    }) || []
  const subTitle2 = extractProperty(object, "subTitle2") as UndStr

  const companyText = extractProperty(object, "companyText") as UndStr
  const groupCompanies = (extractProperty(object, "groupMembers") as Array<ContentList>) || []
  const entrepreneursTitle = extractProperty(object, "entrepreneursTitle") as UndStr
  const groupEntrepreneurs =
    (extractProperty(object, "groupEntrepreneurs") as Array<ContentList>) || []
  const groupPartnersTitle = extractProperty(object, "groupPartnersTitle") as UndStr
  const groupPartners = (extractProperty(object, "groupPartners") as Array<ContentList>) || []
  const mapTitle = extractProperty(object, "mapTitle") as UndStr
  const address = (extractProperty(object, "map") as any)?.address?.coordinates as Address
  const titleAboveProperty = extractProperty(object, "titleAboveProperty") as string
  const newTitleAboveProperty = extractProperty(object, "newTitleAboveProperty") as string

  const textAboveProperty = extractProperty(object, "textAboveProperty") as string

  const seeMoreTitle = extractProperty(object, "seeMoreTitle") as string
  const seeMoreTitleNew = extractProperty(object, "seeMoreTitleNew") as string
  const textUnderneathTitle = extractProperty(object, "textUnderneathTitle") as string
  const hideLeadForm = (extractProperty(object, "hideLeadForm") as boolean) || false
  const eligibilityFormBlock = extractProperty(object, "eligibilityFormBlock") || []
  const formTitle = extractProperty(eligibilityFormBlock?.[0], "blockTitle") as UndStr
  const formIcon = extractProperty(eligibilityFormBlock?.[0], "titleIcon") as UndStr
  const formLogo = extractProperty(eligibilityFormBlock?.[0], "logo") as UndStr
  const formObject = { formTitle, formIcon, formLogo }
  const buildingCharacteristicsSelectionNew =
    extractProperty(object, "buildingCharacteristicsSelectionNew") || []
  const buildingCharacteristicsSelection = buildingCharacteristicsSelectionNew.map((el: any) =>
    extractProperty(el, "getToKnowItem"),
  )
  const apartmentThumbnail = extractProperty(object, "apartmentThumbnail") as UndStr
  const apartmentGalleryArray = extractProperty(object, "apartmentGallery", true) || []
  const apartmentGallery = apartmentGalleryArray
    .filter((el: any) => el.url !== undefined)
    .map((el: any) => el.url) as string[]
  const apartmentVirtualTourLink = extractProperty(object, "apartmentVirtualTourLink")?.[0]
    ?.url as UndStr
  const apartmentVirtualTourLinkName = extractProperty(object, "apartmentVirtualTourLink")?.[0]
    ?.name as UndStr
  let apartmentContractsArray =
    extractProperty(object, "eligibilityDocumentsAndContracts", true) || []
  if (apartmentContractsArray.length === 0) {
    apartmentContractsArray = extractProperty(object, "apartmentContracts", true) || []
  }
  const apartmentContracts = apartmentContractsArray
    .filter((el: any) => el.url !== undefined)
    .map((el: any) => el.url) as string[]

  const apartmentsDrawingsAndTypeObject = extractProperty(object, "apartmentsDrawingsAndType") || []
  const apartmentsDrawing = apartmentsDrawingsAndTypeObject.map((el: any) => {
    const select = extractProperty(el, "selectType")
    const drawingRaw = extractProperty(el, "drawings")
    // drawings is a media picker — extract URL from mediaItems if available
    const drawing =
      typeof drawingRaw === "string"
        ? drawingRaw
        : drawingRaw?.mediaItems?.[0]?.url || drawingRaw?.url || drawingRaw
    return { select, drawing }
  }) as { select: string; drawing: string }[]
  const priorityNumber = extractProperty(object, "bmbyId") as number | string
  const priorityID = String(priorityNumber) as UndStr

  const bmbyMeetingNumber = extractProperty(object, "bmbyMeetingId") as string | number
  const bmbyMeetingId = String(bmbyMeetingNumber) as UndStr

  const contactAnchor = extractProperty(object, "contactAnchor") as string | undefined
  const leaveDetailsButton = extractProperty(object, "leaveDetailsButton") as string | undefined
  const moreInfoButtonObject = extractProperty(object, "moreInfoButton") || []
  const leadFormBMBYIdString = extractProperty(object, "leadFormBMBYId") as string | undefined
  const leadFormBMBYId = leadFormBMBYIdString ? Number(leadFormBMBYIdString) : undefined
  const moreInfoButton = moreInfoButtonObject[0] as { url?: string; name?: string } | undefined
  const projectButton = extractProperty(object, "projectButton") || []
  const galleriesObject = extractProperty(object, "imageGalleriesForRentalProperties") || []
  const galleriesForRental = galleriesObject[0]?.properties || []
  const imageGaleriesBlocks = galleriesForRental[0]?.propertyValue?.blocks || []
  const imageGalleries = imageGaleriesBlocks.map((el: any) => ({
    apartmentThumbnail: extractProperty(el, "apartmentThumbnail"),
    apartmentGallery: extractProperty(el, "apartmentGallery", true)?.map((el: any) => el.url),
    numberOfRooms: Number(extractProperty(el, "numberOfRooms")?.match(/\d+/)?.[0]),
  })) as {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }[]
  // Per-apartment marketing text (apartmentMarketingText block list)
  const marketingTextsObject =
    extractProperty(object, "apartmentMarketingText") ||
    extractProperty(object, "apartmentMarketingTexts") ||
    []
  const marketingTextsMap: Record<number, string> = {}
  const marketingBlocks = Array.isArray(marketingTextsObject) ? marketingTextsObject : []
  marketingBlocks.forEach((el: any) => {
    const aptId = Number(
      extractProperty(el, "apartmentID") || extractProperty(el, "apartmentNumber"),
    )
    const text = (extractProperty(el, "marketingText") as string) || undefined
    if (aptId && text) {
      marketingTextsMap[aptId] = text
    }
  })

  // Project-level general info text
  const generalProjectInfo = extractProperty(object, "generalProjectInfo") as string | undefined

  const emailForLead = extractProperty(object, "emailForLead") as string | undefined

  const rentProjectContactTextObject = extractProperty(object, "rentProjectContactText") as
    | any
    | undefined
  const rentProjectContactText: RentProjectContactText =
    rentProjectContactTextObject &&
    mapFirstBlockProperties<RentProjectContactText>(rentProjectContactTextObject, {
      contactButton: { source: "contactButton", type: "value" },
      leadButton: { source: "leadButton", type: "value" },
      buttonColor: { source: "buttonColor", type: "value" },
      meetingButton: { source: "meetingButton", type: "value" },
      callButton: { source: "callButton", type: "links" },
    })

  const rentProjectInterestedNum = extractProperty(object, "rentProjectInterestedNum") as
    | number
    | undefined

  const noAvailableApartmentsBannerObject = extractProperty(
    object,
    "noAvailableApartmentsBanner",
  ) as any | undefined
  const noAvailableApartmentsBannerBlock = noAvailableApartmentsBannerObject?.[0]
  const noAvailableApartmentsBanner = noAvailableApartmentsBannerBlock
    ? {
        title: extractProperty(noAvailableApartmentsBannerBlock, "title") as string | undefined,
        text: extractProperty(noAvailableApartmentsBannerBlock, "text") as string | undefined,
        button: (extractProperty(noAvailableApartmentsBannerBlock, "button") || [])?.[0] as
          | { name?: string; url?: string }
          | undefined,
      }
    : undefined

  return {
    rentProjectFAQ,
    customerReviews,
    rentProjectJoinBlock,
    rentProjectAdvantages,
    rentProjectVisualization,
    rentProjectBenefits,
    rentProjectFOMO,
    rentProjectHeroBanner,
    imageGalleries,
    leadFormBMBYId,
    contactAnchor,
    leaveDetailsButton,
    moreInfoButton,
    url: object?.url as UndStr,
    lotteryWays,
    lotterRegisterTitle,
    parent,
    selectProjectsToShow,
    lottery,
    officeInfo,
    projectType,
    hideHeaderAndFooter,
    dynamicBlocks,
    heroBanner,
    moreInfo,
    projectTitle,
    city,
    country,
    legalNote,
    bmbyId,
    propertyDesignations,
    groupSites,
    projectStatuses,
    textHTML,
    price,
    priceCurrency,
    priceText,
    titleGetToKnowTheEnvironment,
    numbers: {
      blockBottom,
      blockContent,
      blockTitle,
    },
    middleBanner,
    subTitle,
    contentHTML,
    gallery,
    galleryTitle,
    gallerySubTitle,
    subTitle2,
    architectsOfficeTitle,
    architectsOffice,
    companyText,
    groupCompanies,
    entrepreneursTitle,
    groupEntrepreneurs,
    groupPartnersTitle,
    groupPartners,
    phone,
    phoneName,
    contactPhone,
    miniSite,
    virtualTourLink,
    virtualTourLinkName,
    getToKnowTheEnvironment,
    mapTitle,
    address,
    titleAboveProperty,
    newTitleAboveProperty,
    textAboveProperty,
    seeMoreTitle,
    seeMoreTitleNew,
    textUnderneathTitle,
    hideLeadForm,
    numbersNew: blockContentNew,
    buildingCharacteristicsTitle,
    buildingCharacteristicsText,
    buildingCharacteristicsSelection,
    apartmentsGeneral: {
      apartmentThumbnail,
      apartmentGallery,
      apartmentVirtualTourLink,
      apartmentVirtualTourLinkName,
      apartmentContracts,
      apartmentsDrawing,
    },
    priorityID,
    bmbyMeetingId,
    formObject: eligibilityFormBlock?.[0],
    apartments,
    projectButton,
    apartmentsTypes,
    companyId,
    emailForLead,
    rentProjectContactText,
    rentProjectInterestedNum,
    noAvailableApartmentsBanner,
    marketingTextsMap,
    generalProjectInfo,
    blocks,
  }
}

export function extractSingleProject(data: GetPageQuery, companyId?: number): ProjectPage {
  const object = data.page
  return extractProject(object, companyId)
}

function extractContentArray(data: Nodes, propertyName: string): SimpleContent[] {
  return (
    data
      .map((el) => {
        const properties = el?.namedProperties as any
        const value = properties[propertyName].value as string | undefined
        const id = el?.id!
        const key = el?.key
        if (value) {
          return { value, id, key }
        }
        return null
      })
      .filter((item): item is SimpleContent => item !== null) || []
  )
}

function createFilter(
  label: string,
  options: SimpleContent[],
  locale: string | undefined,
  all?: string,
  allEng?: string,
): ProjectFilter {
  return {
    label,
    options:
      locale === "he"
        ? [{ id: 0, value: all || "הכל" }, ...options]
        : [{ id: 0, value: allEng || "Everything" }, ...options],
  }
}

function extractFilterOptions(data: Nodes, type: string, propertyName: string): SimpleContent[] {
  return (
    data
      .filter((node) => node?.contentType?.alias === type)
      .flatMap((el) => extractContentArray([el], propertyName)) || []
  )
}

export function extractFilters(
  data: Nodes,
  projects?: AllProjects,
  locale?: string,
  landings?: LandingEntity[],
): ProjectFilters {
  // Extracting options
  const designations = extractFilterOptions(data, "propertyDesignation", "Designation")
  const statuses = extractFilterOptions(data, "projectStatus", "statusLisi")

  const subsidiaries = extractFilterOptions(data, "groupSite", "siteName")
  const cities = extractFilterOptions(data, "groupPartner12", "partnerName")
  const countries = extractFilterOptions(data, "groupPartner1", "partnerName")

  if (projects) {
    // Creating unique sets of IDs
    const allowedCities = new Set([
      ...projects.flatMap((project) => project.city.map((status) => status.id)),
      ...(landings || []).flatMap((l) => l.city.map((c) => c.id)),
    ])
    const allowedCountries = new Set(
      projects.flatMap((project) => project.country.map((status) => status.id)),
    )
    const allowedStatuses = new Set([
      ...projects.flatMap((project) => project.projectStatuses.map((status) => status.id)),
      ...(landings || []).flatMap((l) => l.projectStatuses.map((s) => s.id)),
    ])
    const allowedSubsidiaries = new Set(
      projects.flatMap((project) => project.groupSites.map((sub) => sub.key)),
    )
    const allowedDesignations = new Set([
      ...projects.flatMap((project) => project.propertyDesignations.map((des) => des.id)),
      ...(landings || []).flatMap((l) => l.propertyDesignations.map((d) => d.id)),
    ])
    // Filtering the options
    const filteredStatuses = statuses.filter((status) => allowedStatuses.has(status.id))
    const filteredSubsidiaries = subsidiaries.filter((subsidiary) =>
      allowedSubsidiaries.has(subsidiary.key),
    )

    const filteredDesignations = designations.filter((designation) =>
      allowedDesignations.has(designation.id),
    )
    const filteredCities = cities.filter((designation) => allowedCities.has(designation.id))
    const filteredCountries = countries.filter((designation) =>
      allowedCountries.has(designation.id),
    )
    return {
      designations: createFilter(
        locale === "he" ? "ייעוד נכס" : "Property Designation",
        filteredDesignations,
        locale,
        "כל הפרויקטים",
        "All Projects",
      ),
      subsidiaries: createFilter(
        locale === "he" ? "זרועות פעילות" : "Group Divisions",
        filteredSubsidiaries,
        locale,
      ),
      statuses: createFilter(
        locale === "he" ? "סטטוס" : "Project Status",
        filteredStatuses,
        locale,
      ),
      cities: createFilter(locale === "he" ? "ערים" : "Cities", filteredCities, locale),
      countries: createFilter(locale === "he" ? "מדינות" : "Country", filteredCountries, locale),
    }
  } else {
    return {
      designations: createFilter(
        locale === "he" ? "ייעוד נכס" : "Property Designation",
        designations,
        locale,
      ),
      subsidiaries: createFilter(
        locale === "he" ? "זרועות פעילות" : "Group Divisions",
        subsidiaries,
        locale,
      ),
      statuses: createFilter(locale === "he" ? "סטטוס" : "Project Status", statuses, locale),
      cities: createFilter(locale === "he" ? "ערים" : "Cities", cities, locale),
      countries: createFilter(locale === "he" ? "מדינה" : "Country", countries, locale),
    }
  }

  // Returning the filters
}

export type AllProjects = {
  id?: number | null
  url?: string | null
  thumbnail?: string
  desktopImage?: string
  projectType?: string

  projectTitle?: string
  country: Partial<ContentList>[]
  city: Partial<ContentList>[]
  groupSites: Partial<ContentList>[]
  groupMembers: Partial<ContentList>[]
  propertyDesignations: Partial<ContentList>[]
  projectStatuses: Partial<ContentList>[]
  moreInfo?: string
  parent: Partial<ContentList>[]
  projectFields: Partial<ContentList>[]
  residentialUnits?: number
  squareMeter?: number
}[]

export function extractAllProjects(data: Nodes): AllProjects {
  return (
    data
      .filter((node) => node?.contentType?.alias === "project")
      .map((node) => {
        const id = node?.id
        const object = node?.namedProperties as IProject
        const thumbnail = object.thumbnailImageForLobbyPage?.mediaItems[0]?.url
        const url = node?.url
        const parent = object.projectParent?.contentList as Partial<ContentList>[]

        const moreInfo = object.moreInfo?.value
        const projectType = object.projectType?.value
        const projectTitle = object.projectTitle?.value
        const desktopImageBlock = object.heroBanner?.blocks?.[0]?.contentProperties?.[0]
          ?.value as BasicMediaPicker
        const desktopImage = desktopImageBlock?.mediaItems[0]?.url
        const mobileImageBlock = object.heroBanner?.blocks?.[0]?.contentProperties?.[1]
          ?.value as BasicMediaPicker
        const mobileImage = mobileImageBlock?.mediaItems[0]?.url

        const country = object.country?.contentList as Partial<ContentList>[]
        const city = object.city?.contentList as Partial<ContentList>[]
        const groupSites = object.groupSIte?.contentList as Partial<ContentList>[]
        const groupMembers = object.groupMembers?.contentList as Partial<ContentList>[]
        const propertyDesignations = object.propertyDesignations
          ?.contentList as Partial<ContentList>[]
        const projectStatuses = object.projectStatus?.contentList as Partial<ContentList>[]
        const projectFields = object.projectFields?.contentList as Partial<ContentList>[]
        const squareMeter = object.squareMeter?.value
        const residentialUnits = object.residentialUnits?.value
        return {
          id,
          thumbnail,
          url,
          projectType,
          projectTitle,
          country,
          groupMembers,
          city,
          groupSites,
          propertyDesignations,
          projectStatuses,
          desktopImage,
          parent,
          moreInfo,
          mobileImage,
          projectFields,
          squareMeter,
          residentialUnits,
        }
      }) || []
  )
}

type FilterPassed = {
  country?: number
  city?: number
  subsidiary?: string | string[]
  status?: number
  designation?: number
  member?: string
  field?: number
}

export function extractProjectsAndFilters(
  data: Nodes,
  filter: FilterPassed = {},
  locale: string,
  nonMain?: boolean,
  landingNodes?: Nodes,
  /**
   * Slugs of projects that redirect to a marketing landing page on this site
   * (ASH-1941). Removed before the filters are derived, so a redirected project is
   * absent from the lobby entirely rather than merely unrendered: it contributes no
   * facet options and no counts.
   */
  excludeProjectSlugs?: Set<string>,
) {
  let projects = extractAllProjects(data)

  if (excludeProjectSlugs?.size) {
    projects = projects.filter(
      (project) => !excludeProjectSlugs.has(getLastPath(project.url || "")),
    )
  }
  // Filter logic

  if (filter.member) {
    projects = projects.filter((project) =>
      project.groupMembers.some((sub) => sub.key === filter.member),
    )
  }
  if (filter.country) {
    projects = projects.filter((project) =>
      project.country.some((sub) => sub.id === filter.country),
    )
  }
  if (filter.city) {
    projects = projects.filter((project) => project.city.some((sub) => sub.id === filter.city))
  }
  if (filter.subsidiary) {
    if (Array.isArray(filter.subsidiary)) {
      projects = projects.filter((project) =>
        project.groupSites.some((sub) => filter.subsidiary?.includes(sub.key as string)),
      )
    } else {
      projects = projects.filter((project) =>
        project.groupSites.some((sub) => sub.key === filter.subsidiary),
      )
    }
  }

  if (filter.field) {
    projects = projects.filter((project) =>
      project.projectFields.some((sub) => sub.id === filter.field),
    )
  }

  // if (filter.member) {
  //   projects = projects.filter((project) =>
  //     project.groupSites.some((sub) => sub.id === filter.subsidiary)
  //   )
  // }

  if (filter.status) {
    projects = projects.filter((project) =>
      project.projectStatuses.some((status) => status.id === filter.status),
    )
  }

  if (filter.designation) {
    projects = projects.filter((project) =>
      project.propertyDesignations.some((des) => des.id === filter.designation),
    )
  }
  projects = projects.filter((project) => project.projectType !== "זכאים")

  if (
    !filter.country &&
    !filter.city &&
    !filter.designation &&
    !filter.status &&
    !filter.field &&
    nonMain
  ) {
    projects = []
  }
  const landings = extractLandings(
    landingNodes && landingNodes.length > 0 ? landingNodes : data,
    filter,
    locale,
  )
  const filters = extractFilters(data, projects, locale, landings)

  return { projects, filters, landings }
}

// --- Marketing Landing Pages ---

export type LandingEntity = {
  kind: "landing"
  id?: number | null
  title: string
  thumbnail: string
  url?: string | null
  city: Partial<ContentList>[]
  propertyDesignations: Partial<ContentList>[]
  projectStatuses: Partial<ContentList>[]
  groupSites: Partial<ContentList>[]
  residentialUnits?: number
}

export function extractLandings(
  data: Nodes,
  filter: FilterPassed = {},
  locale: string,
): LandingEntity[] {
  let landings: LandingEntity[] = data
    .filter((node) => node?.contentType?.alias === "marketingLandingPage")
    .map((node) => {
      const properties = node?.namedProperties as IMarketingLandingPage
      return {
        kind: "landing" as const,
        id: node?.id,
        title: (properties.pageTitle?.value as string) || "",
        thumbnail: properties.Thumbnail?.mediaItems?.[0]?.url || "",
        url: (node as any)?.urlSegment ? `/${(node as any).urlSegment}` : node?.url,
        city: (properties.city?.contentList as Partial<ContentList>[]) || [],
        propertyDesignations:
          (properties.propertyDesignations?.contentList as Partial<ContentList>[]) || [],
        projectStatuses: (properties.projectStatus?.contentList as Partial<ContentList>[]) || [],
        groupSites: (properties.groupSite?.contentList as Partial<ContentList>[]) || [],
        residentialUnits: properties.residentialUnits?.value,
      }
    })

  // Apply same filters as projects
  if (filter.subsidiary) {
    if (Array.isArray(filter.subsidiary)) {
      landings = landings.filter((l) =>
        l.groupSites.some((sub) => filter.subsidiary?.includes(sub.key as string)),
      )
    } else {
      landings = landings.filter((l) => l.groupSites.some((sub) => sub.key === filter.subsidiary))
    }
  }
  if (filter.city) {
    landings = landings.filter((l) => l.city.some((c) => c.id === filter.city))
  }
  if (filter.designation) {
    landings = landings.filter((l) =>
      l.propertyDesignations.some((d) => d.id === filter.designation),
    )
  }
  if (filter.status) {
    landings = landings.filter((l) => l.projectStatuses.some((s) => s.id === filter.status))
  }

  return landings
}
